import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Building2, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { ClientModel } from '@/domain/models/client.js';
import type { ListAiModels } from '@/domain/use-cases/ai-models.js';
import { DataTable, EmptyState, ErrorState, LoadingState } from '@/presentation/components/index.js';
import type { DataTableColumn } from '@/presentation/components/index.js';
import type { ClientController } from '@/presentation/controllers/client-controller.js';
import type { SapConnectionController } from '@/presentation/controllers/sap-connection-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useClients } from '@/presentation/hooks/use-clients.js';
import { ClientFormDialog } from '@/presentation/pages/clients/components/dialogs/client-form-dialog.js';
import { resolveClientPermissions } from '@/presentation/pages/clients/utils/resolve-client-permissions.js';
import { BRAND_NAVY } from '@/shared/constants/brand.js';
import { primaryCtaGradient } from '@/shared/utils/brand-gradient.js';
import { formatDateShort } from '@/shared/utils/format-date.js';

interface Props {
    controller: ClientController;
    sapConnectionController: SapConnectionController;
    listAiModelsUseCase: ListAiModels;
}

export function ClientListPage({ controller, sapConnectionController, listAiModelsUseCase }: Props) {
    const { translate, useAuthStore } = useApp();
    const currentUser = useAuthStore((s) => s.user);
    const { canCreateClient } = resolveClientPermissions(currentUser);
    const { clients, isLoading, error, search, setSearch, reload } = useClients(controller);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [dialogClientId, setDialogClientId] = useState<string | undefined>(undefined);

    const openCreate = () => {
        setDialogMode('create');
        setDialogClientId(undefined);
        setDialogOpen(true);
    };

    const openEdit = (clientId: string) => {
        setDialogMode('edit');
        setDialogClientId(clientId);
        setDialogOpen(true);
    };

    const handleSaved = () => {
        reload();
    };

    const columns: DataTableColumn<ClientModel>[] = useMemo(
        () => [
            {
                key: 'name',
                header: translate('clients.name'),
                sortValue: (client) => client.name,
                render: (client) => (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        {client.logoUrl ? (
                            <Box component="img" src={client.logoUrl} alt="" sx={{ width: 32, height: 32, borderRadius: 1, objectFit: 'cover' }} />
                        ) : (
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    bgcolor: `${BRAND_NAVY}1A`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Building2 size={14} style={{ color: BRAND_NAVY }} />
                            </Box>
                        )}
                        <Typography variant="body2" fontWeight={500}>
                            {client.name}
                        </Typography>
                    </Stack>
                )
            },
            {
                key: 'abbreviation',
                header: translate('clients.abbreviation'),
                sortValue: (client) => client.abbreviation,
                render: (client) => (
                    <Chip label={client.abbreviation} size="small" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', color: 'text.secondary', fontSize: 12 }} />
                )
            },
            {
                key: 'activeProjects',
                header: translate('clients.activeProjects'),
                sortValue: (client) => client.activeProjects,
                render: (client) => (
                    <Typography variant="body2" color="text.secondary">
                        {client.activeProjects}
                    </Typography>
                )
            },
            {
                key: 'createdAt',
                header: translate('clients.createdAtLabel'),
                sortValue: (client) => client.createdAt,
                render: (client) => (
                    <Typography variant="body2" color="text.secondary">
                        {formatDateShort(client.createdAt)}
                    </Typography>
                )
            }
        ],
        [translate]
    );

    const gradientBtn = {
        textTransform: 'none' as const,
        background: primaryCtaGradient,
        color: 'white',
        '&:hover': { opacity: 0.9, background: primaryCtaGradient }
    };

    if (isLoading && clients.length === 0) {
        return <LoadingState />;
    }
    if (error) {
        return <ErrorState message={error} onRetry={reload} />;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                        {translate('clients.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {translate('clients.manageClients')}
                    </Typography>
                </Box>
                {canCreateClient && (
                    <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate} disableElevation sx={gradientBtn}>
                        {translate('clients.new')}
                    </Button>
                )}
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                    placeholder={translate('clients.searchByNameOrAbbr')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search size={16} />
                            </InputAdornment>
                        )
                    }}
                />
            </Stack>

            {clients.length === 0 ? (
                <EmptyState
                    icon={<Building2 size={24} />}
                    title={translate('clients.noClientsRegistered')}
                    message={translate('clients.startByRegisteringFirstClient')}
                    action={
                        canCreateClient ? (
                            <Button variant="contained" onClick={openCreate} disableElevation sx={gradientBtn}>
                                {translate('clients.registerFirstClient')}
                            </Button>
                        ) : undefined
                    }
                />
            ) : (
                <DataTable columns={columns} rows={clients} getRowKey={(client) => client.id} onRowClick={(client) => openEdit(client.id)} />
            )}

            <ClientFormDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={handleSaved}
                controller={controller}
                sapConnectionController={sapConnectionController}
                listAiModelsUseCase={listAiModelsUseCase}
                mode={dialogMode}
                clientId={dialogClientId}
            />
        </Box>
    );
}
